import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
	activateUser,
	deactivateUser,
	removeUser,
	userUpdate,
} from '../../api';
import {
	createToken,
	deleteToken,
	getRoles,
	getTeam,
	getTokens,
	resendToken,
} from '../../api/company';

import { getUserFullName } from '../../components/helpers/functions';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import { AvatarGenerated } from '../../components/Avatar';
import Button from '../../components/Button';
import {
	DataTable,
	FilterDropdown,
	StackableModal,
} from '../../components/nextgen';
import EllipsisDropdown, {
	EllipsisDropdownItem,
} from '../../components/EllipsisDropdown';

import useAsync from '../../hooks/useAsync';
import useLocal from '../../hooks/useLocal';
import useModal from '../../hooks/useModal';
import {
	createPowerUser,
	deletePowerUser,
	getAllPowerUsers,
	updatePowerUser,
} from '../../api/power-users';

const DEFAULT_USER_FORM = {
	email: '',
	role_id: 'da13e70e-6713-4ec6-bfa6-018c5e2c4c04',
	first_name: '',
	last_name: '',
};

const DEFAULT_POWER_USER_FORM = {
	email: '',
	firstName: '',
	lastName: '',
};

const Team = () => {
	const navigate = useNavigate();

	const { tab } = useParams();

	// User account modal state - null = closed, {} = add mode, {id, ...} = edit mode
	const [userAccountModal, setUserAccountModal] = useState(null);
	const [userForm, setUserForm] = useState(DEFAULT_USER_FORM);

	// Power user modal state - null = closed, {} = add mode, {id, ...} = edit mode
	const [powerUserModal, setPowerUserModal] = useState(null);
	const [powerUserForm, setPowerUserForm] = useState(DEFAULT_POWER_USER_FORM);

	const { setOpen: setDeleteModalOpen, setClose: setDeleteModalClose } =
		useModal('deleteModal');

	const { get } = useLocal();
	const localUser = get('user');

	const { value: team, execute: refreshTeam } = useAsync(getTeam);
	const { value: powerUsers, execute: refreshPowerUsers } =
		useAsync(getAllPowerUsers);
	const { value: tokens, execute: refreshTokens } = useAsync(getTokens);
	const { value: roles, execute: refreshRoles } = useAsync(getRoles);
	const { status: creatingToken, execute: onCreateToken } = useAsync(
		createToken,
		{
			immediate: false,
			showNotification: {
				success: 'A user has been create.  Email sent.',
				error: 'raw',
			},
		},
	);
	const { status: activatingUser, execute: onActivateUser } = useAsync(
		activateUser,
		{
			immediate: false,
			showNotification: { success: 'User activated', error: 'raw' },
		},
	);
	const { status: deactivatingUser, execute: onDeactivateUser } = useAsync(
		deactivateUser,
		{
			immediate: false,
			showNotification: { success: 'User deactivated', error: 'raw' },
		},
	);

	const { execute: onDeletePowerUser } = useAsync(deletePowerUser, {
		immediate: false,
		showNotification: { success: 'Power user deleted' },
	});

	const { execute: onCreatePowerUser, status: creatingPowerUser } = useAsync(
		createPowerUser,
		{
			immediate: false,
			showNotification: {
				success: 'Power user created',
				error: 'raw',
			},
		},
	);

	const { execute: onUpdatePowerUser, status: updatingPowerUser } = useAsync(
		updatePowerUser,
		{
			immediate: false,
			showNotification: {
				success: 'Power user updated',
				error: 'raw',
			},
		},
	);

	const { execute: onResendToken } = useAsync(resendToken, {
		immediate: false,
		showNotification: { success: 'Email sent', error: 'raw' },
	});

	const { execute: onDeleteToken } = useAsync(deleteToken, {
		immediate: false,
		showNotification: { success: 'Invitation deleted', error: 'raw' },
	});

	const { execute: onRemoveUser } = useAsync(removeUser, {
		immediate: false,
		showNotification: { success: 'User removed' },
	});

	const { execute: onUpdateUser, status: updatingRole } = useAsync(userUpdate, {
		immediate: false,
		showNotification: { success: 'User updated' },
	});

	useEffect(() => {
		if (userAccountModal === null) {
			setUserForm(DEFAULT_USER_FORM);
			refreshTeam(null, null, { silent: true });
			refreshTokens(null, null, { silent: true });
		}
	}, [userAccountModal]);

	const activeTeam = team?.rows?.filter((u) => u.active);
	const inactiveTeam = team?.rows?.filter((u) => !u.active);

	// Category filter state derived from URL tab param
	const currentCategory = tab || 'active';

	const categoryOptions = [
		{
			value: 'active',
			label: `Active (${activeTeam?.length || 0})`,
		},
		{
			value: 'inactive',
			label: `Inactive (${inactiveTeam?.length || 0})`,
		},
	];

	const handleCategoryChange = (value) => {
		if (value === 'active') {
			navigate('/manage/team');
		} else {
			navigate(`/manage/team/${value}`);
		}
	};

	// Get data source based on category
	const source = useMemo(() => {
		if (!tab || tab === 'active') {
			return activeTeam || [];
		}
		if (tab === 'inactive') {
			return inactiveTeam || [];
		}
		return [];
	}, [tab, activeTeam, inactiveTeam]);

	// Build columns based on current tab
	const columns = useMemo(() => {
		return [
			{
				key: 'avatar',
				header: '',
				sortable: false,
				width: '50px',
				render: (row) => (
					<AvatarGenerated
						size="xs"
						id={getUserFullName({
							firstName: row?.firstName,
							lastName: row?.lastName,
							...row,
						})}
					/>
				),
			},
			{
				key: 'email',
				header: 'Email',
				render: (row) => row?.email || '',
			},
			{
				key: 'name',
				header: 'Name',
				sortable: false,
				render: (row) => {
					return row.firstName ||
						row.lastName ||
						row.first_name ||
						row.last_name
						? getUserFullName(row)
						: 'Not provided';
				},
			},
			{
				key: 'role',
				header: 'Role',
				render: (row) => row?.role?.displayName || '',
			},
		];
	}, []);

	// Pending users columns
	const pendingColumns = [
		{
			key: 'avatar',
			header: '',
			sortable: false,
			width: '50px',
			render: (row) => (
				<AvatarGenerated
					size="xs"
					id={row?.email || 'pending'}
				/>
			),
		},
		{
			key: 'email',
			header: 'Email',
			render: (row) => row?.email || '',
		},
		{
			key: 'token',
			header: 'Code',
			render: (row) => row?.token || '',
		},
		{
			key: 'role',
			header: 'Role',
			render: (row) => row?.role?.displayName || '',
		},
		{
			key: 'expires',
			header: 'Expires',
			render: (row) => row?.expires || '',
		},
	];

	// Row actions based on current tab
	const renderRowActions = (row) => {
		if (!tab || tab === 'active') {
			if (row.id === localUser.id) return null;
			return (
				<EllipsisDropdown>
					<EllipsisDropdownItem
						onClick={() => {
							setUserForm({
								email: row?.email || '',
								role_id: row?.role_id || DEFAULT_USER_FORM.role_id,
								first_name: row?.firstName || row?.first_name || '',
								last_name: row?.lastName || row?.last_name || '',
							});
							setUserAccountModal({ id: row?.id });
						}}
					>
						Edit User
					</EllipsisDropdownItem>
					<EllipsisDropdownItem
						danger
						onClick={() =>
							setDeleteModalOpen({
								title: 'Deactivate user?',
								description:
									'This user will no longer be able to access the system.',
								confirmText: 'Deactivate',
								onConfirm: () =>
									onDeactivateUser({ id: row?.id }, () => {
										setDeleteModalClose();
										refreshTeam(null, null, { silent: true });
									}),
							})
						}
					>
						Deactivate
					</EllipsisDropdownItem>
				</EllipsisDropdown>
			);
		}

		if (tab === 'inactive') {
			return (
				<EllipsisDropdown>
					<EllipsisDropdownItem
						onClick={() =>
							onActivateUser({ id: row?.id }, () =>
								refreshTeam(null, null, { silent: true }),
							)
						}
					>
						Reactivate
					</EllipsisDropdownItem>
					<EllipsisDropdownItem
						danger
						onClick={() =>
							setDeleteModalOpen({
								title: 'Delete user?',
								description:
									'This action cannot be undone. The user will be permanently removed.',
								confirmText: 'Delete User',
								onConfirm: () =>
									onRemoveUser({ id: row?.id }, () => {
										setDeleteModalClose();
										refreshTeam(null, null, { silent: true });
									}),
							})
						}
					>
						Delete User
					</EllipsisDropdownItem>
				</EllipsisDropdown>
			);
		}

		return null;
	};

	// Pending users row actions
	const renderPendingRowActions = (row) => {
		return (
			<EllipsisDropdown>
				<EllipsisDropdownItem onClick={() => onResendToken({ id: row?.id })}>
					Resend Invite
				</EllipsisDropdownItem>
				<EllipsisDropdownItem
					danger
					onClick={() =>
						setDeleteModalOpen({
							title: 'Delete invite?',
							description:
								'This action cannot be undone. The invite will be permanently removed.',
							confirmText: 'Delete Invite',
							onConfirm: () =>
								onDeleteToken({ id: row?.id }, () => {
									setDeleteModalClose();
									refreshTokens(null, null, { silent: true });
								}),
						})
					}
				>
					Delete Invite
				</EllipsisDropdownItem>
			</EllipsisDropdown>
		);
	};

	// Power Users table columns
	const powerUserColumns = [
		{
			key: 'avatar',
			header: '',
			sortable: false,
			width: '50px',
			render: (row) => (
				<AvatarGenerated
					size="xs"
					id={getUserFullName({
						firstName: row?.firstName,
						lastName: row?.lastName,
						...row,
					})}
				/>
			),
		},
		{
			key: 'email',
			header: 'Email',
			render: (row) => row?.email || '',
		},
		{
			key: 'name',
			header: 'Name',
			sortable: false,
			render: (row) => {
				return row.firstName || row.lastName
					? getUserFullName(row)
					: 'Not provided';
			},
		},
		{
			key: 'assetCount',
			header: 'Assigned Assets',
			render: (row) => row?.assetCount || 0,
		},
	];

	// Power Users row actions
	const renderPowerUserRowActions = (row) => {
		return (
			<EllipsisDropdown>
				<EllipsisDropdownItem
					onClick={() => {
						setPowerUserForm({
							email: row?.email || '',
							firstName: row?.firstName || '',
							lastName: row?.lastName || '',
						});
						setPowerUserModal({ id: row?.id });
					}}
				>
					Edit Power User
				</EllipsisDropdownItem>
				<EllipsisDropdownItem
					danger
					onClick={() =>
						setDeleteModalOpen({
							title: 'Delete power user?',
							description:
								'This action cannot be undone. The power user will no longer have access to assigned assets.',
							confirmText: 'Delete Power User',
							onConfirm: () =>
								onDeletePowerUser(row?.id, () => {
									setDeleteModalClose();
									refreshPowerUsers(null, null, { silent: true });
								}),
						})
					}
				>
					Delete Power User
				</EllipsisDropdownItem>
			</EllipsisDropdown>
		);
	};


	const getPageTitle = () => {
		if (!tab || tab === 'active') return 'Active Accounts';
		if (tab === 'inactive') return 'Inactive';
		return 'Team';
	};

	return (
		<>
			<MainContent maxWidth="max-w-7xl">
				<PageHeading divider={false}>Manage Team</PageHeading>

				<div className="mb-4">
					<div className="flex items-center justify-between mb-4">
						<h2 className="text-lg font-semibold text-gray-900">
							Team
							{powerUsers?.length > 0 && (
								<span className="text-gray-500 font-normal pl-1">
									({powerUsers.length})
								</span>
							)}
						</h2>
						<div className="flex items-center gap-2">
							<FilterDropdown
								label="Category"
								placeholder="Select category"
								value={currentCategory}
								options={categoryOptions}
								onChange={handleCategoryChange}
								clearable={false}
							/>
							<Button
								onClick={() => {
									setUserForm(DEFAULT_USER_FORM);
									setUserAccountModal({});
								}}
							>
								Add User Account
							</Button>
						</div>
					</div>

					<DataTable
						columns={columns}
						data={source}
						keyField="id"
						sortable={true}
						defaultSort={{ key: 'email', direction: 'asc' }}
						rowActions={renderRowActions}
						emptyTitle={`No ${getPageTitle().toLowerCase()} found`}
						emptyDescription="Users will appear here once added"
					/>
				</div>

				{/* Pending Users Section */}
				{tokens?.rows?.length > 0 && (
					<div className="mt-8">
						<div className="flex items-center justify-between mb-4">
							<h2 className="text-lg font-semibold text-gray-900">
								Pending Invites{' '}
								<span className="text-gray-500 font-normal">
									({tokens.rows.length})
								</span>
							</h2>
						</div>
						<DataTable
							columns={pendingColumns}
							data={tokens.rows}
							keyField="id"
							sortable={true}
							defaultSort={{ key: 'email', direction: 'asc' }}
							rowActions={renderPendingRowActions}
							emptyTitle="No pending invites"
							emptyDescription="Pending invites will appear here"
						/>
					</div>
				)}

				{/* Power Users Section */}
				<div className="mt-8">
					<div className="flex items-center justify-between mb-4">
						<h2 className="text-lg font-semibold text-gray-900">
							Power Users{' '}
							{powerUsers?.length > 0 && (
								<span className="text-gray-500 font-normal">
									({powerUsers.length})
								</span>
							)}
						</h2>
						<Button
							onClick={() => {
								setPowerUserForm(DEFAULT_POWER_USER_FORM);
								setPowerUserModal({});
							}}
						>
							Add Power User
						</Button>
					</div>
					<DataTable
						columns={powerUserColumns}
						data={powerUsers || []}
						keyField="id"
						sortable={true}
						defaultSort={{ key: 'email', direction: 'asc' }}
						rowActions={renderPowerUserRowActions}
						onRowClick={(row) => navigate(`/manage/team/power-user/${row.id}`)}
						emptyTitle="No power users found"
						emptyDescription="Power users can view assigned assets without full account access"
					/>
				</div>
			</MainContent>

			{/* User Account Modal - Add/Edit */}
			<StackableModal
				open={userAccountModal !== null}
				onClose={() => {
					setUserAccountModal(null);
					setUserForm(DEFAULT_USER_FORM);
				}}
				title={userAccountModal?.id ? 'Edit User' : 'Add User Account'}
				size="md"
			>
				<div className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Email <span className="text-red-500">*</span>
						</label>
						<input
							type="email"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							placeholder="email@example.com"
							onChange={(e) =>
								setUserForm({ ...userForm, email: e.target.value })
							}
							value={userForm.email}
							disabled={!!userAccountModal?.id}
						/>
						{!!userAccountModal?.id && (
							<p className="mt-1 text-xs text-gray-500">
								Email cannot be changed for existing users
							</p>
						)}
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Role <span className="text-red-500">*</span>
						</label>
						<select
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							onChange={(e) =>
								setUserForm({ ...userForm, role_id: e.target.value })
							}
							value={userForm.role_id}
						>
							{roles?.map((role) => (
								<option key={role.id} value={role.id}>
									{role.display_name}
								</option>
							))}
						</select>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							First Name{' '}
							<span className="text-gray-400 text-xs">(optional)</span>
						</label>
						<input
							type="text"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							placeholder="John"
							onChange={(e) =>
								setUserForm({ ...userForm, first_name: e.target.value })
							}
							value={userForm.first_name}
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Last Name{' '}
							<span className="text-gray-400 text-xs">(optional)</span>
						</label>
						<input
							type="text"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							placeholder="Doe"
							onChange={(e) =>
								setUserForm({ ...userForm, last_name: e.target.value })
							}
							value={userForm.last_name}
						/>
					</div>

					<div className="flex justify-end gap-3 pt-4">
						<Button
							type="secondary"
							onClick={() => {
								setUserAccountModal(null);
								setUserForm(DEFAULT_USER_FORM);
							}}
						>
							Cancel
						</Button>
						<Button
							disabled={
								!userForm.email ||
								!userForm.role_id ||
								creatingToken === 'pending' ||
								updatingRole === 'pending'
							}
							onClick={() => {
								if (userAccountModal?.id) {
									// Edit mode - update user
									onUpdateUser(
										{
											id: userAccountModal.id,
											role_id: userForm.role_id,
											first_name: userForm.first_name,
											last_name: userForm.last_name,
										},
										() => {
											setUserAccountModal(null);
											setUserForm(DEFAULT_USER_FORM);
											refreshTeam(null, null, { silent: true });
										},
									);
								} else {
									// Add mode - create token/invite
									onCreateToken(
										{
											email: userForm.email,
											role_id: userForm.role_id,
											first_name: userForm.first_name,
											last_name: userForm.last_name,
										},
										() => {
											setUserAccountModal(null);
											setUserForm(DEFAULT_USER_FORM);
										},
									);
								}
							}}
						>
							{creatingToken === 'pending' || updatingRole === 'pending'
								? 'Saving...'
								: userAccountModal?.id
									? 'Save Changes'
									: 'Send Invite'}
						</Button>
					</div>
				</div>
			</StackableModal>

			{/* Power User Modal - Add/Edit */}
			<StackableModal
				open={powerUserModal !== null}
				onClose={() => {
					setPowerUserModal(null);
					setPowerUserForm(DEFAULT_POWER_USER_FORM);
				}}
				title={powerUserModal?.id ? 'Edit Power User' : 'Add Power User'}
				size="md"
			>
				<div className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Email <span className="text-red-500">*</span>
						</label>
						<input
							type="email"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							placeholder="email@example.com"
							onChange={(e) =>
								setPowerUserForm({ ...powerUserForm, email: e.target.value })
							}
							value={powerUserForm.email}
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							First Name{' '}
							<span className="text-gray-400 text-xs">(optional)</span>
						</label>
						<input
							type="text"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							placeholder="John"
							onChange={(e) =>
								setPowerUserForm({ ...powerUserForm, firstName: e.target.value })
							}
							value={powerUserForm.firstName}
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Last Name{' '}
							<span className="text-gray-400 text-xs">(optional)</span>
						</label>
						<input
							type="text"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							placeholder="Doe"
							onChange={(e) =>
								setPowerUserForm({ ...powerUserForm, lastName: e.target.value })
							}
							value={powerUserForm.lastName}
						/>
					</div>

					<div className="flex justify-end gap-3 pt-4">
						<Button
							type="secondary"
							onClick={() => {
								setPowerUserModal(null);
								setPowerUserForm(DEFAULT_POWER_USER_FORM);
							}}
						>
							Cancel
						</Button>
						<Button
							disabled={
								!powerUserForm.email ||
								creatingPowerUser === 'pending' ||
								updatingPowerUser === 'pending'
							}
							onClick={() => {
								if (powerUserModal?.id) {
									// Edit mode
									onUpdatePowerUser(
										{
											id: powerUserModal.id,
											...powerUserForm,
										},
										() => {
											setPowerUserModal(null);
											setPowerUserForm(DEFAULT_POWER_USER_FORM);
											refreshPowerUsers(null, null, { silent: true });
										},
									);
								} else {
									// Add mode
									onCreatePowerUser(powerUserForm, () => {
										setPowerUserModal(null);
										setPowerUserForm(DEFAULT_POWER_USER_FORM);
										refreshPowerUsers(null, null, { silent: true });
									});
								}
							}}
						>
							{creatingPowerUser === 'pending' || updatingPowerUser === 'pending'
								? 'Saving...'
								: powerUserModal?.id
									? 'Save Changes'
									: 'Add Power User'}
						</Button>
					</div>
				</div>
			</StackableModal>
		</>
	);
};

export default Team;
